import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { CacheKeys } from '../../../../lib/cache'
import { deactivateMember } from '../../../../models/invitation'
import type { OrgMember } from '../../../../models/user'

export function DeactivateUserDialog({
  member,
  open,
  onOpenChange,
}: {
  member: OrgMember
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [loading, setLoading] = useState(false)
  const queryClient = useQueryClient()

  async function handleDeactivate() {
    setLoading(true)
    try {
      await deactivateMember(member.orgUser.id)
      await queryClient.invalidateQueries({ queryKey: [CacheKeys.CURRENT_USER] })
      toast.success(`${member.firstName || member.email} has been deactivated.`)
      onOpenChange(false)
    } catch (err: unknown) {
      console.error(err)
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to deactivate member'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Deactivate Member</DialogTitle>
          <DialogDescription>
            Are you sure you want to deactivate <strong>{member.firstName || member.email}</strong>? They will
            no longer be able to access this organization.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeactivate}
            disabled={loading}
            loading={loading ? 'Deactivating...' : undefined}
          >
            Deactivate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
